// stores/driverLicenseStore.js
'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateDriverLicenseList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/driver-license/search');
};

export const useDriverLicenseList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .setOrder(['license_name'], true)
      .addJoin('company', ['name']);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);
  const key = useMemo(() => ['/driver-license/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const rows = Array.isArray(data) ? data : data?.data || [];
  const total = data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? rows.length;
  return {
    data: rows,
    totalRecord: total,
    error,
    isLoading
  };
};

export const getDriverLicenseById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/driver-license/search${getTzParam()}`, payload);

    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch driver license by ID:', error);
    throw error;
  }
};

// CRUD
export const createDriverLicense = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/driver-license/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverLicenseList();
    return response.data;
  } catch (error) {
    console.error('Error creating driver license:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateDriverLicense = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/driver-license/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverLicenseList();
    return response.data;
  } catch (error) {
    console.error('Error updating driver license:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteDriverLicense = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver-license/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateDriverLicenseList();
    return response.data;
  } catch (error) {
    console.error('Error deleting driver license:', error?.response?.data ?? error);
    throw error;
  }
};

export const uploadDriverLicenseDetailFile = async (licenseDetailId, file, type) => {
  if (!licenseDetailId) throw new Error('licenseDetailId is required');
  if (!file) throw new Error('file is required');
  if (!['front', 'back', 'document'].includes(type)) {
    throw new Error("type must be 'front', 'back', or 'document'");
  }

  try {
    const fd = new FormData();
    fd.append('file', file);
    const url = `/driver-license-detail/uploadfile/${licenseDetailId}?imageType=${type}`;

    const response = await fleetApi.post(url, fd, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    console.error(`Error uploading driver license ${type}:`, error?.response?.data ?? error);
    throw error;
  }
};
export const deleteDriverLicenseDetailImage = async (id, type, fileName) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver-license-detail/images/${id}${getTzParam()}&imageType=${type}&file=${fileName}`);
    return response.data;
  } catch (err) {
    console.error('Failed to delete Driver license detail image:', err);
    throw err;
  }
};

export const getDriverLicenseDetailFile = async (licenseDetailId, type) => {
  if (!licenseDetailId) throw new Error('licenseDetailId is required');
  if (!['front', 'back', 'document'].includes(type)) {
    throw new Error("type must be 'front', 'back', or 'document'");
  }
  try {
    const url = `/driver-license-detail/images/${licenseDetailId}?imageType=${type}`;
    const response = await fleetApi.get(url);
    const imageUrl = response.data?.data || response.data || '';
    return imageUrl;
  } catch (error) {
    console.error(`Error fetching driver license image ${type}:`, error?.response?.data ?? error);
    return '';
  }
};
